import { HttpClient } from '@angular/common/http';
import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DataTableDirective } from 'angular-datatables';
import { Subject } from 'rxjs';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { PermissionsService } from 'src/app/services/permissions.service';
import { DialogService } from 'src/app/services/dialog.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { ZileLibereService } from './zile-libere.service';
import Swal from 'sweetalert2';
import { EditZileComponent } from './edit-zile/edit-zile.component';
import { AddZileComponent } from './add-zile/add-zile.component';
import { DatePipe } from '@angular/common';
import { IZileLibere } from './zile-libere.interface';

@Component({
    selector: 'app-zile-libere',
    templateUrl: './zile-libere.component.html',
    styleUrls: ['./zile-libere.component.scss'],
})
export class ZileLibereComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;
    isLoading = false;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    dtLink =
        environment.interop.basePath +
        environment.interop.deps.nomenclatoare.zileLibere.listDT;

    constructor(
        public permissionsService: PermissionsService,
        private http: HttpClient,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private zileLibereService: ZileLibereService,
        private datePipe: DatePipe,
    ) { }

    ngOnInit(): void {
        this.renderDT();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            responsive: true,
            pageLength: 25,
            searching: false,
            serverSide: true,
            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                },
                {
                    title: 'Nume',
                    data: 'off_day_name',
                },
                {
                    title: 'Dată',
                    data: 'date',
                    render: (data: any, type: any, row: any, met: any) => {
                        return `
                        <span>${this.datePipe.transform(row.date, 'dd.MM.yyyy')}</span>
                        `;
                    }
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (data: any, type: any, row: any, met: any) => {
                        return `
                        <div class="d-flex align-center justify-content-left">
                            <button type="button" class="action-btn edit me-1">
                                <img class="btn-img" src="/assets/icons/edit.svg" alt="edit"/>
                            </button>
                            <button type="button" class="action-btn delete me-1">
                                <img class="btn-img" src="/assets/icons/delete.svg" alt="delete"/>
                            </button>
                        </div>
                        `;
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const editIcon = lastTd?.querySelector('.edit') as HTMLElement;
                const deleteIcon = lastTd?.querySelector('.delete') as HTMLElement;

                if (editIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'deps-module.settings.zile-libere.update'
                        )
                    ) {
                        editIcon.style.display = 'none';
                    }
                    editIcon.addEventListener('click', () => {
                        this.openEditDialog(data);
                    });
                }

                if (deleteIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'deps-module.settings.zile-libere.delete'
                        )
                    ) {
                        deleteIcon.style.display = 'none';
                    }
                    deleteIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.deleteZileLibere(id);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(resetPaging = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(resetPaging);
        });
    }

    openAddDialog() {
        const dialogRef = this.dialog.open(AddZileComponent, {
            ...this.dialogService.getDialogConfig(),
        });
        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Zi Liberă adăugată cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openEditDialog(data: IZileLibere) {
        const dialogRef = this.dialog.open(EditZileComponent, {
            ...this.dialogService.getDialogConfig(),
            data,
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Actualizare cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    deleteZileLibere(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'Nu o să puteți recupera datele odată cu ștergerea lor!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                // we do this because we need to have some control on activity
                this.deleteAction(id);
            }
        });
    }

    deleteAction(id: number) {
        this.isLoading = true;
        this.zileLibereService.deleteZileLibere(id).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Zi liberă ștearsă cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err: { error: { errors: { message: any[] } } }) => {
                this.isLoading = false;
                let msg;
                try {
                    msg = err.error.errors.message[0];
                } catch (error) {
                    msg = helper.dialogConfig.generalMessages.error;
                }

                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.deleteAction(id);
                    }
                });
            }
        );
    }
}
